import { DateTime } from "luxon";
import User from "@/interfaces/IUser";
import { checkIfUserHasRoles } from "@/helpers/UserHelpers";
import { removeUndefined } from "@/helpers/utils";

interface MontageMeetingOwnership {
  authorId: string | null;
  csmOwnerId: string | null;
}

/**
 * Règle absolue : tout ADMIN et tout CSM gèrent n'importe quel rendez-vous
 * montage, sans être ni auteur ni propriétaire. Ce repli ne sert que lorsque
 * l'API n'a pas transmis son propre `canManage` — elle seule connaît la
 * liaison commerciale de l'entreprise, que ce helper ne peut pas deviner.
 */
export const canManageMontageMeeting = (
  user: User,
  meeting: MontageMeetingOwnership,
) =>
  checkIfUserHasRoles(user, ["ADMIN", "CSM"]) ||
  (checkIfUserHasRoles(user, ["AE"]) &&
    (meeting.authorId === user.id || meeting.csmOwnerId === user.id));

/** Même périmètre que l'API : le SDR consulte ses rattachements actifs SDR. */
export const canSeeMontageMeetings = (
  user: User,
  company: { ae: { userId: string } | null; sdr?: { userId: string } | null },
) => {
  if (checkIfUserHasRoles(user, ["ADMIN", "CSM"])) return true;
  if (user.roles?.includes("SDR") && !user.roles.includes("AE")) {
    return company.sdr?.userId === user.id;
  }
  return company.ae?.userId === user.id;
};

/** Gris du thème pour un rendez-vous qui ne m'appartient pas (gray-300/900). */
const MASKED_BACKGROUND = "#d8e2ef";
const MASKED_TEXT = "#344050";

/** Libellé d'un rendez-vous sans entreprise ni titre (spec 2026-10-08, §2.7). */
export const UNTITLED_MEETING = "Rendez-vous sans titre";

/** Rendez-vous sans entreprise : bleu pâle, bord en pointillés (voir
 *  `.fc-event-standalone`), distinct des statuts et du gris des masqués. */
const STANDALONE_BACKGROUND = "#eaf2ff";
const STANDALONE_BORDER = "#8db6f2";
const STANDALONE_TEXT = "#0f4fa8";

/**
 * Transforme un rendez-vous de l'API en événement FullCalendar.
 *
 * Trois formes : masqué, sans entreprise, d'entreprise. L'API masque les rendez-vous ouverts par
 * « Visibilité calendrier » : elle n'envoie alors que la date et le nom du
 * commercial. Ce helper ne lit donc RIEN d'autre sur un rendez-vous masqué,
 * même si le champ était présent — c'est ce que vérifient ses tests.
 *
 * Sans `companyId`, le clic ne mène nulle part : `onEventClick` et
 * `onDayModalRowClick` en dépendent tous les deux, la fiche ne s'ouvre pas.
 */
export const meetingEndAt = (meeting: {
  startAt: string;
  endAt?: string;
  durationMinutes?: number;
}) =>
  meeting.endAt ??
  DateTime.fromISO(meeting.startAt)
    .plus({ minutes: meeting.durationMinutes ?? 60 })
    .toISO({ includeOffset: false }) ??
  undefined;

export const toCalendarEvent = (meeting: any) => {
  if (meeting.masked) {
    return {
      title: meeting.ownerName || "Rendez-vous",
      backgroundColor: MASKED_BACKGROUND,
      textColor: MASKED_TEXT,
      start: meeting.startAt,
      end: meetingEndAt(meeting),
      masked: true as const,
    };
  }

  // Sans entreprise : pas de fiche à ouvrir, pas de statut pour la couleur. Le
  // titre saisi tient lieu de tout, et l'événement porte de quoi ouvrir sa
  // petite fiche (MeetingDetailsModal).
  if (!meeting.companyId) {
    const title = meeting.title || UNTITLED_MEETING;
    return {
      id: meeting.id,
      title: `${title}${
        meeting.author ? ` (par ${meeting.author.fullname})` : ""
      }`,
      backgroundColor: STANDALONE_BACKGROUND,
      borderColor: STANDALONE_BORDER,
      textColor: STANDALONE_TEXT,
      classNames: ["fc-event-standalone"],
      start: meeting.startAt,
      end: meetingEndAt(meeting),
      standalone: true as const,
      meetingTitle: title,
      meetingType: meeting.type ?? null,
      authorId: meeting.authorId ?? null,
      authorName: meeting.author?.fullname ?? null,
    };
  }

  const {
    startAt: start,
    company,
    companyId,
    companyCourses,
    author,
  } = meeting;
  const end = meetingEndAt(meeting);

  return {
    title: `${
      companyCourses?.length
        ? `🎓 ${companyCourses
            .map((companyCourse: any) => companyCourse.course?.name)
            .filter(Boolean)
            .join(", ")} / `
        : ""
    }${removeUndefined(
      `${company?.ae?.user.fullname} / ${company?.status?.name} / ${company?.name} / ${company?.sdr?.user.fullname}`,
    )}${author ? ` (par ${author.fullname})` : ""}`,
    authorName: author?.fullname ?? null,
    backgroundColor: company?.status?.backgroundColor,
    textColor: company?.status?.textColor,
    companyId,
    companyCourseIds: companyCourses?.map(
      (companyCourse: any) => companyCourse.id,
    ),
    start,
    end,
  };
};
